import {
  Firestore,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
} from "firebase/firestore/lite";
import { firebaseDb } from "../firebase/client-app";
import {
  DbBusinessMember,
  DbBusinessMemberRole,
  InviteMemberData,
} from "./models";
import { axiosInstance } from "../rest/axios";
import { getCookie } from "cookies-next";

export class MemberService {
  private businessMembersRef(firestore: Firestore, businessId: string) {
    return collection(
      firestore,
      "businesses/" + businessId + "/business_members",
    );
  }

  async addBusinessMember(
    businessId: string,
    userId: string,
    role: string,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    const existingMember = await getDoc(
      doc(this.businessMembersRef(firestore, businessId), userId),
    );
    if (existingMember.exists()) {
      throw new Error("Member already exists");
    }

    await setDoc(doc(this.businessMembersRef(firestore, businessId), userId), {
      id: userId,
      role: role,
      notifications: {
        booking: true,
        message: true,
      },
    });
  }

  async updateBusinessMemberRole(
    businessId: string,
    data: DbBusinessMember,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(
      doc(this.businessMembersRef(firestore, businessId), data.id),
      { role: data.role },
    );
  }

  async removeBusinessMember(
    businessId: string,
    memberId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await deleteDoc(
      doc(this.businessMembersRef(firestore, businessId), memberId),
    );
  }

  async getBusinessMember(
    businessId: string,
    userId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessMember> {
    const memberData = await getDoc(
      doc(this.businessMembersRef(firestore, businessId), userId),
    );
    return memberData.data() as DbBusinessMember;
  }

  async getBusinessMembers(
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessMember[]> {
    const membersQuery = query(this.businessMembersRef(firestore, businessId));
    const querySnapshot = await getDocs(membersQuery);
    return querySnapshot.docs.map((doc) => doc.data() as DbBusinessMember);
  }

  async inviteMember(
    businessId: string,
    data: InviteMemberData,
  ): Promise<void> {
    try {
      const response = await axiosInstance.post(
        "/business/member/invite",
        {
          business_id: businessId,
          name: data.name,
          phone: data.phone,
          email: data.email,
          role: data.role,
        },
        {
          headers: { Authorization: getCookie("Authorization")?.toString() },
        },
      );
      return response.data;
    } catch (error: any) {
      throw new Error("Failed to invite member");
    }
  }
}

export const memberService = new MemberService();
